import { debounce } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { getMouseBlock } from 'src/editor/utils/block';
import { useBlockLocked } from 'src/hooks/block/use-block-locked';
import { isMindNodeFocused } from 'src/mind-map/utils/mind-node-util';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import { $appUiStateCache, setAppUiState } from 'src/services/app';
import { getDropInfo, reSetDropInfo, setDropInfo } from 'src/services/app/hook/use-drop-info';
import { useObservableBlock } from 'src/services/rxjs-redux/hook';
import { isMindMap } from 'src/utils/block-type-utils';
import { getDynamicPageId } from 'src/utils/getPageId';
import {
  cursorIsInAside,
  getPanel,
  ID_HOVER_MENU,
  ID_RECORD_HOVER_MENU,
  isInRightPanel,
} from './helper';
import { onOver } from './on-over';
import type { TocInfo } from './types';
import { useOnDropBlock } from './use-on-drop-block';
import { useOnDropFile } from './use-on-drop-file';

// 最小偏移量
const OFFSET = 5;
const INIT: {
  x: number;
  y: number;
  clickId?: string;
  syncId?: string;
  viewId?: string;
  groupValue?: string;
  subGroupValue?: string;
  sourceRalationParentId?: string;
  mindMapId?: string;
  dragTocType?: TocInfo['dragTocType'];
  dragTocDeep?: TocInfo['dragTocDeep'];
  offsetX: number;
  offsetY: number;
} = {
  x: 0,
  y: 0,
  offsetX: 0,
  offsetY: 0,
};

/** 拖拽 */
export const useDnd = () => {
  const onDropBlock = useOnDropBlock();
  const onDropFile = useOnDropFile();
  const pageId = getDynamicPageId(); // 思维导图在右测需要用到这个来判断
  const blockType = useObservableBlock(pageId, (block) => block?.type);
  const startDrag = useRef(INIT);
  const timer = useRef(Infinity);
  const isLocked = useBlockLocked(pageId);

  useEffect(() => {
    const mousedown = (event: MouseEvent) => {
      if (isLocked) return;
      if (document.getElementById('dropDownContent')) return;
      if (document.getElementById('modalContent')) return;
      if (document.activeElement instanceof HTMLElement) {
        // 思维导图节点编辑时不能拖拽
        if (document.activeElement.closest('[data-mind-map-id]') && isMindNodeFocused()) {
          return;
        }
      }

      if (!event.target || event.button !== 0) return;
      let t: HTMLElement | null = event.target as HTMLElement;
      if (t.tagName === 'INPUT') return;

      startDrag.current = {
        x: event.x,
        y: event.y,
        offsetX: 0,
        offsetY: 0,
      };

      if (cursorIsInAside(event.x)) {
        const node = (t as HTMLElement).closest('[data-block-id]') as HTMLElement | null;
        if (node && node.dataset.blockId) {
          startDrag.current.clickId = node.dataset.blockId;
          startDrag.current.dragTocType = Number(node.dataset.tocType ?? -1);
          startDrag.current.dragTocDeep = Number(node.dataset.deep ?? -1);
        }
      } else {
        if (isMindMap(blockType)) {
          if (t.closest('.code-mirror')) {
            return;
          }
          t = t.closest('[data-block-id]');
          if (t === null) return;
          const nodeRect = t.getBoundingClientRect();
          startDrag.current.offsetX = nodeRect.x - event.x;
          startDrag.current.offsetY = nodeRect.y - event.y;
        }

        // for caption and code mirror
        if (
          t.isContentEditable ||
          t.dataset.ignoreDraggable ||
          t.closest('[data-ignore-draggable]') ||
          t.closest('.code-mirror')
        ) {
          return;
        }

        const isRight = isInRightPanel(event.x);
        const panelClass = getPanel(isRight);

        const isHoverMenu = [ID_HOVER_MENU, ID_RECORD_HOVER_MENU, t.dataset.blockId].includes(t.id);
        if (
          !isHoverMenu &&
          document.getElementById(ID_HOVER_MENU) &&
          !t.closest('[data-hide-hover-menu]') &&
          !t.dataset.imageId
        ) {
          return;
        }

        const { blockId, node } = isHoverMenu
          ? { node: t, blockId: t.dataset.blockId }
          : getMouseBlock(event, document.querySelector(`.${panelClass}`));

        if (node?.dataset.draggable === 'true') {
          const { syncId, viewId, groupValue, subGroupValue, mindMapId, relationParentId } =
            node.dataset;

          startDrag.current.clickId = blockId;
          startDrag.current.syncId = syncId;
          startDrag.current.viewId = viewId;
          startDrag.current.groupValue = groupValue;
          startDrag.current.subGroupValue = subGroupValue;
          startDrag.current.mindMapId = mindMapId;
          startDrag.current.sourceRalationParentId = relationParentId;

          setAppUiState({ $focusBlockId: blockId });
        }
      }

      if (startDrag.current.clickId) {
        // 兼容 safari https://stackoverflow.com/questions/47295211/safari-wrong-cursor-when-dragging
        // 这里会导致一个问题，思维导图点击节点，再点击一次无法focus，所以思维导图下不要重写这个方法
        if (isMindMap(blockType)) {
          return;
        }
        document.onselectstart = function () {
          return false;
        };
      }
    };

    // 用 dragover 的话鼠标 样式无法变成 grabbing
    const move = (event: MouseEvent) => {
      const { x, y } = event;
      const { x: initX, y: initY } = startDrag.current;
      if (x < initX + OFFSET && x > initX - OFFSET && y < initY + OFFSET && y > initY - OFFSET) {
        return;
      }
      // 框选的时候不触发拖拽
      if (
        !startDrag.current.clickId ||
        $appUiStateCache.$blockMenuListId ||
        $appUiStateCache.$isSelecting
      ) {
        return;
      }

      if (cursorIsInAside(initX)) {
        // 放到了 move 里头是因为，如果在 mousedown 的话会闪一下
        if (!getDropInfo()?.tocInfo?.dragToc) {
          setDropInfo({
            tocInfo: {
              dragToc: startDrag.current.clickId,
              dragTocType: startDrag.current.dragTocType,
              dragTocDeep: startDrag.current.dragTocDeep,
            },
          });
        }
        const { selectedBlocks } = cache.ui;
        if (selectedBlocks.length) {
          dispatch(
            uiActions.update({
              selectedBlocks: [],
              selectedCells: [],
            })
          );
        }
      } else {
        const { selectedBlocks } = cache.ui;
        const { clickId, syncId, viewId, groupValue, subGroupValue, mindMapId } = startDrag.current;

        if (
          !selectedBlocks.find((item) => {
            const isSelected = item.blockId === clickId && item.viewId === viewId;
            if (!isSelected) return false;

            if (!item.groupValues || !groupValue) return true;
            return Object.keys(item.groupValues).includes(groupValue ?? '');
          })
        ) {
          dispatch(
            uiActions.updateSelectBlocks([
              {
                blockId: clickId,
                syncId,
                viewId,
                groupValues: groupValue
                  ? { [groupValue]: subGroupValue ? [subGroupValue] : [] }
                  : undefined,
                mindMapId,
              },
            ])
          );
        }
      }

      onOver(
        event,
        startDrag.current.offsetX,
        startDrag.current.offsetY,
        startDrag.current.sourceRalationParentId
      );
    };
    const mousemove = debounce(move);

    const mouseup = (e: MouseEvent) => {
      // 兼容 safari https://stackoverflow.com/questions/47295211/safari-wrong-cursor-when-dragging
      document.onselectstart = function () {
        return true;
      };
      if (!startDrag.current.clickId) return;
      startDrag.current = INIT;
      setAppUiState({ $focusBlockId: undefined });

      void onDropBlock(e.altKey);
    };

    // 防止拖拽某些元素 如 link
    const dragstart = (event: DragEvent) => {
      event.preventDefault();
    };

    // 不防抖拖拽文件进页面时线不跟手
    // 如果这里用节流的话，快速移出窗口时 线会不消失，因为会在 dragleave 后还执行
    const over = debounce((event: DragEvent) => {
      // 拖拽文件到目录范围时
      if (cursorIsInAside(event.x)) {
        reSetDropInfo();
        return;
      }

      if ($appUiStateCache.$blockMenuListId) {
        setAppUiState({ $blockMenuListId: '' });
      }
      if (cache.ui.selectedBlocks.length) {
        dispatch(uiActions.updateSelectBlocks([]));
      }

      onOver(event, 0, 0, startDrag.current.sourceRalationParentId);
    });
    const dragover = (event: DragEvent) => {
      event.preventDefault();
      if (document.getElementById('dropDownContent')) return;
      over(event);
    };

    const dragenter = () => {
      if (timer.current !== Infinity) {
        clearTimeout(timer.current);
      }
    };

    const dragleave = (event: DragEvent) => {
      event.preventDefault();
      // 离开浏览器范围时取消
      if (event.x === 0 && event.y === 0) {
        timer.current = window.setTimeout(() => {
          reSetDropInfo();
          timer.current = Infinity;
        }, 100);
      }
    };

    // 从系统拖拽文件释放只会触发 drop
    const drop = (event: DragEvent) => {
      const dropDown = document.getElementById('dropDownContent');
      if (dropDown) {
        const target = event.target as HTMLInputElement;
        if (target.tagName === 'INPUT' && target.type === 'file') return;
      }
      event.preventDefault();
      void onDropFile(event);
    };

    document.addEventListener('mousemove', mousemove);
    document.addEventListener('mousedown', mousedown);
    document.addEventListener('mouseup', mouseup, true);
    document.addEventListener('dragstart', dragstart);
    document.addEventListener('dragenter', dragenter);
    document.addEventListener('dragover', dragover);
    document.addEventListener('dragleave', dragleave);
    document.addEventListener('drop', drop);

    return () => {
      document.removeEventListener('mousemove', mousemove);
      document.removeEventListener('mousedown', mousedown);
      document.removeEventListener('mouseup', mouseup, true);
      document.removeEventListener('dragstart', dragstart);
      document.removeEventListener('dragenter', dragenter);
      document.removeEventListener('dragover', dragover);
      document.removeEventListener('dragleave', dragleave);
      document.removeEventListener('drop', drop);
    };
  }, [blockType, isLocked, onDropBlock, onDropFile]);
};
